<template>
  <div v-if="problemDescriptionPresenter.isLoading.value" class="h-full flex items-center justify-center">
    <LoadingPanel />
  </div>
  <div v-else class="mx-2">
    <div class="flex mt-1 select-none">
      <RedText
        :border_radius="0.25"
        class="mr-3 ml-1"
        :padding="2"
        :text="problemDescriptionPresenter.problemDescription.value.timeLimit + ' sec'"
        :textSize="'text-xs'"
      />
      <RedText
        :border_radius="0.25"
        class="my-auto"
        :padding="2"
        :text="problemDescriptionPresenter.problemDescription.value.memoryLimit + ' KB'"
      />
    </div>
    <div
      ref="problemDomElement"
      class="mt-4 mb-10 description"
      style="user-select: contain"
      v-html="problemDescriptionPresenter.displayedProblemHtml.value"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, watch } from 'vue'

import LoadingPanel from '@/presentation/components/Loading/LoadingPanel.vue'
import RedText from '@/presentation/components/Text/RedText.vue'
import { useConfigStatePresenter } from '@/presentation/composables/config/useConfigStatePresenter'
import { useProblemDescriptionPresenter } from '@/presentation/composables/problem/useProblemDescriptionPresenter'
import { useProblemStatePresenter } from '@/presentation/composables/problem/useProblemStatePresenter'

export default defineComponent({
  components: { RedText, LoadingPanel },
  props: {
    scroll: {
      type: Function,
      required: true
    }
  },
  setup(props, ctx) {
    const problemDescriptionPresenter = useProblemDescriptionPresenter()
    const { problemId } = useProblemStatePresenter()
    const { displayLanguage } = useConfigStatePresenter()
    const problemDomElement = ref<HTMLDivElement>()

    onMounted(() => {
      problemDescriptionPresenter.setProblemDomElement(problemDomElement.value)
      problemDescriptionPresenter.updateProblemHtml()
    })

    watch(
      () => problemDescriptionPresenter.problemDescription.value.html,
      () => {
        problemDescriptionPresenter.setProblemDomElement(problemDomElement.value)
        problemDescriptionPresenter.updateProblemHtml()
        props.scroll()
      }
    )

    watch(
      () => displayLanguage.value,
      () => {
        problemDescriptionPresenter.getProblemDescription(displayLanguage.value, problemId.value)
        problemDescriptionPresenter.setProblemDomElement(problemDomElement.value)
        problemDescriptionPresenter.updateProblemHtml()
        props.scroll()
      }
    )

    return {
      problemDescriptionPresenter,
      problemDomElement
    }
  }
})
</script>
<style lang="postcss" scoped>
.problemDom-text {
  @apply text-black dark:text-dark-text;
}

.problemDom-sqrt {
  @apply border-t border-solid border-black dark:border-dark-200;
}

.problemDom-table {
  @apply border-b border-solid border-black dark:border-dark-200;
}

.description {
  & :deep(h1) {
    @apply text-3xl border-b-2 font-medium text-center text-gray-800 dark:text-dark-text dark:border-dark-200;
  }

  & :deep(h2) {
    @apply text-xl mt-6 mb-1 border-b-2 text-gray-800 dark:text-dark-text dark:border-dark-200;
  }

  & :deep(pre) {
    border-color: #cccccc;
    border-radius: 0.5rem;
    @apply p-3 my-2  border bg-gray-100 border-gray-200 whitespace-pre-wrap text-gray-900 dark:text-dark-text dark:border-dark-200 dark:bg-dark-400;
  }

  & :deep(ul) {
    @apply list-disc pl-12 text-gray-800 dark:text-dark-text;
  }

  & :deep(p) {
    @apply mb-2 mt-2 text-gray-800 dark:text-dark-text;
  }

  & :deep(img) {
    @apply dark:bg-white my-4;
  }

  & :deep(a) {
    @apply underline text-blue-500;
  }

  & :deep(table) {
    @apply my-4 border-2 border-gray-500 dark:border-dark-100;
  }

  & :deep(th) {
    @apply border-2 border-gray-500 dark:border-dark-100;
  }

  & :deep(td) {
    @apply border-2 border-gray-500 dark:border-dark-100;
  }
}
</style>
